import { storage } from '../platform/storage';
import { BALANCE } from '@2k48/shared';
import { AUDIO_MANIFEST } from './catalog';
import type {
  AudioBus,
  AudioBusSettings,
  AudioCue,
  AudioManifest,
  AudioSettings,
  MusicCue,
} from './types';

const SETTINGS_KEY = '2k48.audio.v1';

export const DEFAULT_AUDIO_SETTINGS: AudioSettings = {
  master: { volume: 1, muted: false },
  music: { volume: 0.7, muted: false },
  sfx: { volume: 0.9, muted: false },
};

function clampVolume(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.min(1, Math.max(0, value))
    : fallback;
}

function normaliseBus(value: unknown, fallback: AudioBusSettings): AudioBusSettings {
  const candidate = typeof value === 'object' && value !== null ? value : {};
  return {
    volume: clampVolume(Reflect.get(candidate, 'volume'), fallback.volume),
    muted:
      typeof Reflect.get(candidate, 'muted') === 'boolean'
        ? Boolean(Reflect.get(candidate, 'muted'))
        : fallback.muted,
  };
}

export function normaliseAudioSettings(value: unknown): AudioSettings {
  const candidate = typeof value === 'object' && value !== null ? value : {};
  return {
    master: normaliseBus(Reflect.get(candidate, 'master'), DEFAULT_AUDIO_SETTINGS.master),
    music: normaliseBus(Reflect.get(candidate, 'music'), DEFAULT_AUDIO_SETTINGS.music),
    sfx: normaliseBus(Reflect.get(candidate, 'sfx'), DEFAULT_AUDIO_SETTINGS.sfx),
  };
}

type SettingsListener = (settings: Readonly<AudioSettings>) => void;

/** Shared by every setter that can be applied for preview only. */
export interface WriteOptions {
  /** Default true. False applies the change live but leaves storage alone. */
  persist?: boolean;
}

export class AudioManager {
  private settings = normaliseAudioSettings(DEFAULT_AUDIO_SETTINGS);
  private context?: AudioContext;
  private masterGain?: GainNode;
  private sfxGain?: GainNode;
  private readonly buffers = new Map<AudioCue, AudioBuffer>();
  private readonly loading = new Map<AudioCue, Promise<void>>();
  private readonly listeners = new Set<SettingsListener>();
  private initPromise?: Promise<void>;
  private initialised = false;
  private removeGestureListeners?: () => void;
  private removeVisibilityListener?: () => void;
  private desiredMusic: MusicCue | null = null;
  private activeMusic: MusicCue | null = null;
  private musicElement?: HTMLAudioElement;
  private musicTransition = 0;
  private transitioningTo: MusicCue | null | undefined;

  constructor(private manifest: AudioManifest = AUDIO_MANIFEST) {}

  init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = this.initialise();
    return this.initPromise;
  }

  private async initialise(): Promise<void> {
    this.settings = normaliseAudioSettings(
      await storage.getJson<unknown>(SETTINGS_KEY, DEFAULT_AUDIO_SETTINGS),
    );
    this.initialised = true;
    this.notify();
    this.syncMusic();
    if (typeof document === 'undefined') return;

    const unlock = (): void => {
      void this.unlock().then(() => this.preloadSfx());
    };
    document.addEventListener('pointerdown', unlock, { capture: true, once: true });
    document.addEventListener('keydown', unlock, { capture: true, once: true });
    this.removeGestureListeners = () => {
      document.removeEventListener('pointerdown', unlock, true);
      document.removeEventListener('keydown', unlock, true);
    };

    const onVisibility = (): void => {
      if (document.hidden) {
        void this.context?.suspend().catch(() => {});
        this.musicElement?.pause();
        return;
      }
      if (this.context) void this.context.resume().catch(() => {});
      this.syncMusic();
    };
    document.addEventListener('visibilitychange', onVisibility);
    this.removeVisibilityListener = () =>
      document.removeEventListener('visibilitychange', onVisibility);
  }

  async unlock(): Promise<boolean> {
    await this.init();
    if (typeof window === 'undefined') return false;

    const AudioContextClass = window.AudioContext;
    if (!AudioContextClass) return false;

    if (!this.context) {
      this.context = new AudioContextClass({ latencyHint: 'interactive' });
      this.masterGain = this.context.createGain();
      this.sfxGain = this.context.createGain();
      this.sfxGain.connect(this.masterGain);
      this.masterGain.connect(this.context.destination);
      this.syncBusGains();
    }

    try {
      if (this.context.state !== 'running') await this.context.resume();
    } catch {
      return false;
    }

    this.removeGestureListeners?.();
    this.removeGestureListeners = undefined;
    this.syncMusic();
    return this.context.state === 'running';
  }

  getSettings(): AudioSettings {
    return normaliseAudioSettings(this.settings);
  }

  subscribe(listener: SettingsListener): () => void {
    this.listeners.add(listener);
    listener(this.getSettings());
    return () => this.listeners.delete(listener);
  }

  /**
   * `persist: false` applies a value without writing it to disk.
   *
   * The settings screen needs to be heard before it is committed — a volume you
   * cannot try is a volume you cannot choose — and a preview that survived the
   * player backing out would not be a preview. Playing the change and saving it
   * are therefore two decisions, not one.
   */
  setVolume(bus: AudioBus, volume: number, { persist = true }: WriteOptions = {}): void {
    this.settings[bus].volume = clampVolume(volume, this.settings[bus].volume);
    this.settings = normaliseAudioSettings(this.settings);
    this.settingsChanged(persist);
  }

  setMuted(bus: AudioBus, muted: boolean, { persist = true }: WriteOptions = {}): void {
    this.settings[bus].muted = muted;
    this.settings = normaliseAudioSettings(this.settings);
    this.settingsChanged(persist);
  }

  /** Write whatever is currently in effect. The commit half of a preview. */
  persistSettings(): void {
    void storage.setJson(SETTINGS_KEY, this.settings);
  }

  configure(manifest: AudioManifest): void {
    this.musicTransition += 1;
    this.transitioningTo = undefined;
    this.stopMusicElement();
    this.buffers.clear();
    this.loading.clear();
    this.manifest = manifest;
    this.syncMusic();
  }

  async preloadSfx(cues: readonly AudioCue[] = Object.keys(this.manifest.sfx) as AudioCue[]) {
    if (!(await this.unlock()) || !this.context) return;
    await Promise.all(cues.map((cue) => this.loadCue(cue)));
  }

  play(cue: AudioCue): void {
    void this.playCue(cue);
  }

  private async playCue(cue: AudioCue): Promise<void> {
    if (this.settings.master.muted || this.settings.sfx.muted) return;
    if (!(await this.unlock()) || !this.context || !this.sfxGain) return;

    // Never fetch/decode on the gameplay hot path. A missing buffer means the
    // cue was not prepared yet and is safely skipped.
    const buffer = this.buffers.get(cue);
    if (!buffer) return;

    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.sfxGain);
    source.start();
  }

  playMusic(cue: MusicCue): void {
    this.desiredMusic = cue;
    this.syncMusic();
  }

  stopMusic(): void {
    this.desiredMusic = null;
    this.syncMusic();
  }

  destroy(): void {
    this.removeGestureListeners?.();
    this.removeVisibilityListener?.();
    this.musicTransition += 1;
    this.stopMusicElement();
    this.listeners.clear();
    void this.context?.close().catch(() => {});
    this.context = undefined;
  }

  private async loadCue(cue: AudioCue): Promise<void> {
    if (this.buffers.has(cue)) return;
    const pending = this.loading.get(cue);
    if (pending) return pending;

    const url = this.manifest.sfx[cue];
    if (!url || !this.context) return;

    const task = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Could not preload ${cue}`);
        return response.arrayBuffer();
      })
      .then((data) => this.context?.decodeAudioData(data))
      .then((buffer) => {
        if (buffer) this.buffers.set(cue, buffer);
      })
      .catch(() => {})
      .finally(() => this.loading.delete(cue));
    this.loading.set(cue, task);
    return task;
  }

  private syncMusic(): void {
    if (
      !this.initialised ||
      typeof Audio === 'undefined' ||
      typeof document === 'undefined' ||
      document.hidden
    )
      return;
    const cue = this.desiredMusic;
    const track = cue ? this.manifest.music[cue] : undefined;
    if (this.settings.master.muted || this.settings.music.muted) {
      if (this.transitioningTo !== undefined) {
        this.musicTransition += 1;
        this.transitioningTo = undefined;
      }
      this.musicElement?.pause();
      return;
    }

    if (!cue || !track) {
      if (this.musicElement && this.transitioningTo !== null) this.startMusicTransition(null);
      return;
    }

    if (this.activeMusic !== cue || !this.musicElement) {
      if (this.transitioningTo !== cue) this.startMusicTransition(cue);
      return;
    }

    this.musicElement.volume = this.musicVolume();
    if (!this.musicElement.ended) void this.musicElement.play().catch(() => {});
  }

  /**
   * Scene changes are sequential rather than overlapping: fade the old track
   * to silence, replace it, then raise the new one. The serial makes rapid
   * phase changes safe — a stale transition can never revive its old song.
   */
  private startMusicTransition(cue: MusicCue | null): void {
    const serial = ++this.musicTransition;
    this.transitioningTo = cue;
    void this.transitionMusic(cue, serial).finally(() => {
      if (serial === this.musicTransition) this.transitioningTo = undefined;
    });
  }

  private async transitionMusic(cue: MusicCue | null, serial: number): Promise<void> {
    const outgoing = this.musicElement;
    if (outgoing && !outgoing.paused && !outgoing.ended) {
      const completed = await this.fadeElement(
        outgoing,
        outgoing.volume,
        0,
        BALANCE.audio.musicFadeOutMs,
        serial,
      );
      if (!completed) return;
    }
    if (serial !== this.musicTransition) return;

    this.stopMusicElement();
    if (!cue || this.desiredMusic !== cue) return;

    const track = this.manifest.music[cue];
    if (!track) return;

    const incoming = new Audio(track.src);
    incoming.loop = track.loop;
    incoming.preload = 'auto';
    incoming.volume = 0;
    this.musicElement = incoming;
    this.activeMusic = cue;

    try {
      await incoming.play();
    } catch {
      // Autoplay can still be locked on the first paint. `unlock()` calls
      // `syncMusic()` again from the user's first real gesture.
      incoming.volume = this.musicVolume();
      return;
    }
    if (serial !== this.musicTransition) return;

    const completed = await this.fadeElement(
      incoming,
      0,
      this.musicVolume(),
      BALANCE.audio.musicFadeInMs,
      serial,
    );
    if (completed) incoming.volume = this.musicVolume();
  }

  private fadeElement(
    element: HTMLAudioElement,
    from: number,
    to: number,
    durationMs: number,
    serial: number,
  ): Promise<boolean> {
    if (durationMs <= 0 || typeof requestAnimationFrame === 'undefined') {
      element.volume = to;
      return Promise.resolve(serial === this.musicTransition);
    }

    return new Promise((resolveFade) => {
      const startedAt = performance.now();
      const tick = (now: number): void => {
        if (serial !== this.musicTransition) {
          resolveFade(false);
          return;
        }
        const progress = Math.min(1, Math.max(0, (now - startedAt) / durationMs));
        const smooth = progress * progress * (3 - 2 * progress);
        element.volume = Math.min(1, Math.max(0, from + (to - from) * smooth));
        if (progress >= 1) {
          resolveFade(true);
          return;
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  }

  private musicVolume(): number {
    return this.settings.master.volume * this.settings.music.volume;
  }

  private stopMusicElement(): void {
    if (this.musicElement) {
      this.musicElement.pause();
      this.musicElement.removeAttribute('src');
      this.musicElement.load();
    }
    this.musicElement = undefined;
    this.activeMusic = null;
  }

  private settingsChanged(persist = true): void {
    this.syncBusGains();
    this.syncMusic();
    this.notify();
    if (persist) this.persistSettings();
  }

  private syncBusGains(): void {
    if (!this.masterGain || !this.sfxGain) return;
    this.masterGain.gain.value = this.settings.master.muted ? 0 : this.settings.master.volume;
    this.sfxGain.gain.value = this.settings.sfx.muted ? 0 : this.settings.sfx.volume;
  }

  private notify(): void {
    const snapshot = this.getSettings();
    for (const listener of this.listeners) listener(snapshot);
  }
}

export const audio = new AudioManager();
